// Face matching berjalan 100% di browser (client-side) - foto/video wajah
// karyawan tidak pernah dikirim ke backend, hanya descriptor numerik (128
// float) hasil model ini. Model weight file di-host sendiri di
// /public/models (TIDAK dari CDN) - lihat migration_absensi_wajah_2026-09-03.sql
// utk latar belakang lengkap.
//
// Pipeline (2026-10-05, ditingkatkan dari TinyFaceDetector+Landmark68Tiny):
//   - Pengenalan : SsdMobilenetv1 (deteksi lebih akurat, wajah kecil/miring
//                  tetap tertangkap) + FaceLandmark68Net PENUH (penjajaran wajah
//                  lebih presisi -> descriptor lebih stabil) + FaceRecognitionNet.
//   - Cepat      : TinyFaceDetector + FaceLandmark68Net, tanpa descriptor - utk
//                  deteksi kedipan (liveness) yang perlu ~8 frame/detik.
import * as faceapi from "@vladmandic/face-api";

const MODEL_URL = "/models";

let loadPromise = null;

// loadFaceApiModels - idempotent, aman dipanggil berkali-kali (dipakai baik
// dari halaman admin Enrollment Wajah maupun halaman kiosk).
export function loadFaceApiModels() {
  if (!loadPromise) {
    loadPromise = Promise.all([
      faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
      faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
      faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
      faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
    ]).catch((e) => {
      loadPromise = null; // izinkan "Coba Lagi" memuat ulang
      throw e;
    });
  }
  return loadPromise;
}

// loadKioskModels - model RINGAN saja utk kiosk (2026-10-06, kembali ke
// pipeline lama atas permintaan user: lebih cepat & tak gampang delay di
// tablet). Unduhan jauh lebih kecil drpd loadFaceApiModels (tanpa SSD &
// landmark penuh). Descriptor tetap kompatibel dgn enrollment SSD (uji
// 2026-10-05: wajah sama median 0.126).
let kioskLoadPromise = null;
export function loadKioskModels() {
  if (!kioskLoadPromise) {
    kioskLoadPromise = Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
      faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),
      faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
    ]).catch((e) => {
      kioskLoadPromise = null;
      throw e;
    });
  }
  return kioskLoadPromise;
}

const SSD_OPTIONS = new faceapi.SsdMobilenetv1Options({ minConfidence: 0.5, maxResults: 1 });
const KIOSK_OPTIONS = new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 });

// detectFaceDescriptorFast - pipeline lama kiosk: TinyFaceDetector +
// FaceLandmark68TinyNet + descriptor (~3-4x lebih cepat drpd SSD). Butuh
// loadKioskModels().
export async function detectFaceDescriptorFast(mediaEl) {
  return faceapi
    .detectSingleFace(mediaEl, KIOSK_OPTIONS)
    .withFaceLandmarks(true)
    .withFaceDescriptor();
}
const FAST_OPTIONS = new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.4 });

// detectFaceDescriptor - deteksi + landmark + descriptor pada satu frame.
// Mengembalikan undefined kalau tak ada wajah terdeteksi.
export async function detectFaceDescriptor(mediaEl) {
  return faceapi
    .detectSingleFace(mediaEl, SSD_OPTIONS)
    .withFaceLandmarks()
    .withFaceDescriptor();
}

// detectFacePresence - cek CEPAT ada-tidaknya wajah (detektor tiny saja).
// Dipakai kiosk sbg penyaring hemat daya: model berat (SSD + descriptor)
// hanya dijalankan bila memang ada wajah di depan kamera.
export async function detectFacePresence(mediaEl) {
  return !!(await faceapi.detectSingleFace(mediaEl, FAST_OPTIONS));
}

// detectFaceLandmarksFast - deteksi ringan + landmark (tanpa descriptor),
// cukup cepat utk dipanggil beberapa kali per detik saat cek kedipan.
export async function detectFaceLandmarksFast(mediaEl) {
  return faceapi.detectSingleFace(mediaEl, FAST_OPTIONS).withFaceLandmarks();
}

// euclideanDistance - jarak antar 2 descriptor 128-float. Semakin kecil,
// semakin mirip.
export function euclideanDistance(a, b) {
  let sum = 0;
  for (let i = 0; i < a.length; i++) {
    const d = a[i] - b[i];
    sum += d * d;
  }
  return Math.sqrt(sum);
}

// meanDescriptor - rata-rata beberapa descriptor (beberapa frame berturut-
// turut) -> derau per frame (blur, cahaya berkedip) saling meredam.
export function meanDescriptor(list) {
  const out = new Array(list[0].length).fill(0);
  for (const d of list) for (let i = 0; i < d.length; i++) out[i] += d[i];
  return out.map((v) => v / list.length);
}

// findBestMatch - nearest-neighbor LINTAS SEMUA sampel tiap karyawan (bukan
// dibandingkan ke rata-rata), lebih tahan variasi pose/cahaya. Default
// threshold 0.5 - lebih ketat dari default umum face-api (0.6): di sistem
// terkait payroll, salah-kenali lebih berbahaya drpd gagal-kenali.
//
// margin: jarak karyawan TERDEKAT KEDUA harus lebih jauh minimal `margin` dari
// yang terdekat. Tanpa uji ini, dua orang mirip yg sama-sama di bawah threshold
// bisa saling tertukar; dengan uji ini kasus ragu dianggap belum dikenali
// (dikembalikan sbg { ambiguous: true }) dan kiosk meminta wajah ditahan.
export function findBestMatch(descriptor, dataset, threshold = 0.5, margin = 0) {
  let best = null;
  let second = null; // terdekat kedua, dari karyawan LAIN
  for (const person of dataset) {
    let personMin = Infinity;
    for (const sample of person.Descriptors || []) {
      const distance = euclideanDistance(descriptor, sample);
      if (distance < personMin) personMin = distance;
    }
    if (personMin === Infinity) continue;
    const cand = { Nik: person.Nik, NamaKaryawan: person.NamaKaryawan, distance: personMin };
    if (!best || cand.distance < best.distance) {
      second = best;
      best = cand;
    } else if (!second || cand.distance < second.distance) {
      second = cand;
    }
  }
  if (!best || best.distance > threshold) return null;
  if (margin > 0 && second && second.distance - best.distance < margin) {
    return { ...best, ambiguous: true, secondDistance: second.distance };
  }
  return best;
}

// ── Kualitas wajah ──────────────────────────────────────────────────────
const center = (pts) => ({
  x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
  y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
});

let lumaCanvas = null;
function faceBrightness(mediaEl, box) {
  try {
    if (!lumaCanvas) lumaCanvas = document.createElement("canvas");
    lumaCanvas.width = 24;
    lumaCanvas.height = 24;
    const ctx = lumaCanvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(mediaEl, box.x, box.y, box.width, box.height, 0, 0, 24, 24);
    const px = ctx.getImageData(0, 0, 24, 24).data;
    let sum = 0;
    for (let i = 0; i < px.length; i += 4) sum += 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
    return sum / (px.length / 4);
  } catch (e) {
    return null; // gagal baca piksel -> lewati cek cahaya
  }
}

// faceMetrics - ukuran & pose wajah dari hasil deteksi: lebar wajah relatif
// thd lebar frame, yaw (menoleh; ~0 = lurus, tanda +/- = arah), roll
// (kepala miring, derajat), skor deteksi, dan kecerahan area wajah.
export function faceMetrics(result, mediaEl) {
  const box = result.detection.box;
  const vw = mediaEl.videoWidth || mediaEl.width || 640;
  const lm = result.landmarks;
  const le = center(lm.getLeftEye());
  const re = center(lm.getRightEye());
  const nose = lm.getNose()[3]; // ujung hidung
  const eyeDist = Math.hypot(re.x - le.x, re.y - le.y) || 1;
  return {
    faceWidth: box.width / vw,
    yaw: (nose.x - (le.x + re.x) / 2) / eyeDist,
    roll: (Math.atan2(re.y - le.y, re.x - le.x) * 180) / Math.PI,
    score: result.detection.score,
    luma: faceBrightness(mediaEl, box),
  };
}

// assessFaceQuality - tolak frame yang buruk SEBELUM dicocokkan, dengan alasan
// yang bisa ditampilkan ke karyawan. Mengembalikan null bila layak, atau
// { reason, hint }. opts.maxYaw: batas menoleh (enroll pose samping memakai
// batas lebih longgar); opts.minFaceWidth: batas jarak.
export function assessFaceQuality(result, mediaEl, opts = {}) {
  const { maxYaw = 0.35, minFaceWidth = 0.18, minScore = 0.6 } = opts;
  const m = faceMetrics(result, mediaEl);
  if (m.faceWidth < minFaceWidth) return { reason: "far", hint: "Dekatkan wajah ke kamera" };
  if (m.score < minScore) return { reason: "unclear", hint: "Wajah kurang jelas, tahan sebentar" };
  if (Math.abs(m.yaw) > maxYaw) return { reason: "turned", hint: "Hadapkan wajah lurus ke kamera" };
  if (Math.abs(m.roll) > 20) return { reason: "tilted", hint: "Tegakkan kepala" };
  if (m.luma !== null && m.luma < 55) return { reason: "dark", hint: "Pencahayaan terlalu gelap" };
  if (m.luma !== null && m.luma > 225) return { reason: "bright", hint: "Cahaya terlalu silau" };
  return null;
}

// createLandmarkTracker - pelacak titik wajah CEPAT utk cek kedipan/menoleh:
// kotak wajah dideteksi sesekali (tiap REDETECT_EVERY frame), di antaranya
// hanya model landmark yang dijalankan pada potongan wajah (~7x lebih cepat
// drpd deteksi+landmark tiap frame) -> kedipan singkat tak terlewat.
// Mengembalikan fungsi async (video) -> { landmarks, canvas } | null, dengan
// canvas = potongan wajah (koordinat landmark relatif thd canvas ini).
const REDETECT_EVERY = 8;
// opts.tiny: pakai FaceLandmark68TinyNet (cukup loadKioskModels) — lebih
// ringan, dipakai halaman enrollment sejak 2026-10-07.
export function createLandmarkTracker(opts = {}) {
  let box = null;
  let frame = 0;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  return async (video) => {
    if (!box || frame % REDETECT_EVERY === 0) {
      const det = await faceapi.detectSingleFace(video, FAST_OPTIONS);
      box = det ? det.box : null;
    }
    frame += 1;
    if (!box) return null;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const pad = box.width * 0.15;
    const x = Math.max(0, box.x - pad);
    const y = Math.max(0, box.y - pad);
    const w = Math.min(vw - x, box.width + 2 * pad);
    const h = Math.min(vh - y, box.height + 2 * pad);
    if (w < 40 || h < 40) return null;
    canvas.width = Math.round(w);
    canvas.height = Math.round(h);
    ctx.drawImage(video, x, y, w, h, 0, 0, canvas.width, canvas.height);
    const landmarks = opts.tiny
      ? await faceapi.detectFaceLandmarksTiny(canvas)
      : await faceapi.detectFaceLandmarks(canvas);
    return landmarks ? { landmarks, canvas } : null;
  };
}

// eyeContrast - kontras area mata (simpangan baku / rata-rata kecerahan),
// rata-rata kedua mata. Mata terbuka = kontras tinggi (putih mata vs iris
// gelap); terpejam = hampir rata (kulit kelopak). Landmark HANYA dipakai utk
// menemukan letak mata (andal), bukan bentuk kelopak (kurang peka) -> sinyal
// kedipan kedua yang tak bergantung pada akurasi titik kelopak.
let eyeCanvas = null;
export function eyeContrast(landmarks, canvas) {
  try {
    if (!eyeCanvas) eyeCanvas = document.createElement("canvas");
    eyeCanvas.width = 32;
    eyeCanvas.height = 16;
    const ectx = eyeCanvas.getContext("2d", { willReadFrequently: true });
    let sum = 0;
    for (const eye of [landmarks.getLeftEye(), landmarks.getRightEye()]) {
      const xs = eye.map((p) => p.x);
      const ys = eye.map((p) => p.y);
      const ex = Math.min(...xs);
      const ew = Math.max(...xs) - ex;
      const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
      const eh = Math.max(ew * 0.5, 4);
      ectx.drawImage(canvas, ex - ew * 0.1, cy - eh / 2, ew * 1.2, eh, 0, 0, 32, 16);
      const px = ectx.getImageData(0, 0, 32, 16).data;
      let m = 0;
      let m2 = 0;
      const n = px.length / 4;
      for (let i = 0; i < px.length; i += 4) {
        const g = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
        m += g;
        m2 += g * g;
      }
      m /= n;
      const sd = Math.sqrt(Math.max(0, m2 / n - m * m));
      sum += m > 1 ? sd / m : 0;
    }
    return sum / 2;
  } catch (e) {
    return null;
  }
}

// runFrameLoop - jalankan step async sekali per frame layar (requestAnimation
// Frame), tanpa tumpang tindih. Kembalikan fungsi stop().
export function runFrameLoop(step) {
  let stopped = false;
  (async () => {
    while (!stopped) {
      try {
        await step();
      } catch (e) {
        // frame gagal diproses - lanjut frame berikutnya
      }
      await new Promise((r) => requestAnimationFrame(r));
    }
  })();
  return () => {
    stopped = true;
  };
}

// createBlinkDetector - pendeteksi satu kedipan dari deret EAR per frame.
// Acuan "mata terbuka" = persentil-80 dari ~30 nilai terakhir (tahan lonjakan
// sesaat, menyesuaikan ukuran mata tiap orang). Kedip = turun di bawah 78%
// acuan lalu kembali di atas 90% acuan dalam < 1,5 dtk (mata terpejam lama
// / menyipit tidak dihitung). Dipakai bersama kiosk & enrollment.
// update(ear, nowMs) -> true saat satu kedipan selesai.
export function createBlinkDetector(minBase = 0.15) {
  const hist = [];
  let closedAt = null;
  const update = (ear, now = Date.now()) => {
    hist.push(ear);
    if (hist.length > 30) hist.shift();
    const sorted = [...hist].sort((a, b) => a - b);
    const base = sorted[Math.floor(sorted.length * 0.8)];
    update.openness = base > 0 ? Math.min(1, ear / base) : 1; // utk indikator UI
    if (hist.length < 5 || base < minBase) return false;
    if (closedAt === null) {
      if (ear < base * 0.78) closedAt = now;
      return false;
    }
    if (ear > base * 0.9) {
      const ok = now - closedAt < 1500;
      closedAt = null;
      return ok;
    }
    if (now - closedAt >= 1500) closedAt = null; // terpejam terlalu lama
    return false;
  };
  update.openness = 1;
  return update;
}

// createDualBlinkDetector - kedipan terdeteksi bila SALAH SATU sinyal (EAR
// bentuk kelopak, atau kontras area mata) turun lalu pulih. Mencatat riwayat
// kedua sinyal ternormalisasi (nilai / acuan) utk grafik diagnosa.
// update({landmarks, canvas}) -> true saat kedip.
export function createDualBlinkDetector() {
  const byEar = createBlinkDetector(0.15);
  const byContrast = createBlinkDetector(0.05);
  const history = []; // { ear, con } dlm skala keterbukaan 0..1
  const update = ({ landmarks, canvas }) => {
    const now = Date.now();
    const a = byEar(eyeAspectRatio(landmarks), now);
    const con = eyeContrast(landmarks, canvas);
    const b = con !== null ? byContrast(con, now) : false;
    history.push({ ear: byEar.openness, con: con !== null ? byContrast.openness : null });
    if (history.length > 240) history.shift();
    update.openness = Math.min(byEar.openness, con !== null ? byContrast.openness : 1);
    return a || b;
  };
  update.openness = 1;
  update.history = history;
  return update;
}

// yawFromLandmarks - arah menoleh dari titik wajah (~0 = lurus, tanda = arah).
export function yawFromLandmarks(lm) {
  const le = center(lm.getLeftEye());
  const re = center(lm.getRightEye());
  const nose = lm.getNose()[3];
  const eyeDist = Math.hypot(re.x - le.x, re.y - le.y) || 1;
  return (nose.x - (le.x + re.x) / 2) / eyeDist;
}

// pitchFromLandmarks - arah mendongak/menunduk: jarak vertikal ujung hidung
// ke garis mata, relatif thd jarak antar mata. Nilai mutlak berbeda tiap
// orang, jadi dibandingkan dgn nilai saat wajah lurus (mendongak = turun,
// menunduk = naik).
export function pitchFromLandmarks(lm) {
  const le = center(lm.getLeftEye());
  const re = center(lm.getRightEye());
  const nose = lm.getNose()[3];
  const eyeDist = Math.hypot(re.x - le.x, re.y - le.y) || 1;
  return (nose.y - (le.y + re.y) / 2) / eyeDist;
}

// createTurnChallenge - cadangan liveness bila kedipan sulit terbaca: wajah
// harus menoleh ke KEDUA arah (yaw <= -0.15 dan >= +0.15, urutan bebas).
// Foto datar yang diputar tidak mengubah rasio hidung-mata, jadi tak lolos.
// update(yaw) -> true saat kedua arah sudah tercapai.
export function createTurnChallenge() {
  let neg = false;
  let pos = false;
  const update = (yaw) => {
    if (yaw <= -0.15) neg = true;
    if (yaw >= 0.15) pos = true;
    update.progress = (neg ? 1 : 0) + (pos ? 1 : 0);
    return neg && pos;
  };
  update.progress = 0;
  return update;
}

// eyeAspectRatio - rata-rata EAR kedua mata (Soukupova & Cech 2016): tinggi
// kelopak / lebar mata. Mata terbuka ~0.25-0.35, terpejam turun tajam.
export function eyeAspectRatio(landmarks) {
  const ear = (p) => {
    const d = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
    return (d(p[1], p[5]) + d(p[2], p[4])) / (2 * d(p[0], p[3]) || 1);
  };
  return (ear(landmarks.getLeftEye()) + ear(landmarks.getRightEye())) / 2;
}
